import { useQuery } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';
import { fetchApi } from '../../lib/api';
import { CONFERENCE_TIMEZONE } from '../../lib/timezone';
import { Session } from '../../types/session';
import SessionCard from '../../components/SessionCard';
import SessionDetail from '../../components/SessionDetail';
import SearchBar from '../../components/SearchBar';
import FilterPanel, { FilterState } from '../../components/FilterPanel';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorMessage from '../../components/ErrorMessage';
import { useWebSocket, LiveAnnouncement } from '../../providers/WebSocketProvider';

const DISMISSED_KEY = 'dismissed_announcements';
const AUTO_DISMISS_MS = 30_000;

function getDismissedIds(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function announcementId(a: LiveAnnouncement): string {
  return `${a.subject}::${a.body}`;
}

function AnnouncementBanner({
  announcement,
  onDismiss,
}: {
  announcement: LiveAnnouncement;
  onDismiss: () => void;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timerRef.current = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [onDismiss]);

  const accentColor =
    announcement.priority === 'high'
      ? 'bg-red-600 border-red-700'
      : announcement.priority === 'low'
      ? 'bg-blue-600 border-blue-700'
      : 'bg-usc-garnet border-red-900';

  return (
    <div
      className={`${accentColor} text-white rounded-xl shadow-lg overflow-hidden animate-fade-in`}
      role="alert"
    >
      {announcement.imageUrl && (
        <img
          src={announcement.imageUrl}
          alt=""
          className="w-full max-h-48 object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      <div className="p-4 flex items-start gap-4">
        <div className="flex-shrink-0 mt-0.5">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">{announcement.subject}</p>
          <p className="text-sm mt-0.5 opacity-90 whitespace-pre-wrap">{announcement.body}</p>
        </div>
        <button
          onClick={onDismiss}
          className="flex-shrink-0 text-white/80 hover:text-white transition-colors"
          aria-label="Dismiss announcement"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// Helper to get the time slot key for a session (e.g., "9:00 AM")
function getTimeSlotKey(session: Session): string {
  return new Date(session.startTime).toLocaleTimeString('en-US', {
    timeZone: CONFERENCE_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

// Group sessions by their start time slot
function groupConcurrentSessions(sessions: Session[]): Session[][] {
  if (sessions.length === 0) return [];
  
  const slotMap = new Map<string, Session[]>();
  
  for (const session of sessions) {
    const key = getTimeSlotKey(session);
    if (!slotMap.has(key)) {
      slotMap.set(key, []);
    }
    slotMap.get(key)!.push(session);
  }
  
  // Sort groups by earliest start time
  return Array.from(slotMap.values()).sort((a, b) => 
    new Date(a[0].startTime).getTime() - new Date(b[0].startTime).getTime()
  );
}

export default function ScheduleView() {
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<FilterState>({
    timeRange: '',
    location: '',
  });
  const { isConnected, latestAnnouncement, dismissAnnouncement } = useWebSocket();
  const [visibleAnnouncement, setVisibleAnnouncement] = useState<LiveAnnouncement | null>(null);

  useEffect(() => {
    if (!latestAnnouncement) return;
    const id = announcementId(latestAnnouncement);
    if (!getDismissedIds().has(id)) {
      setVisibleAnnouncement(latestAnnouncement);
    }
  }, [latestAnnouncement]);

  const handleDismiss = () => {
    if (visibleAnnouncement) {
      const id = announcementId(visibleAnnouncement);
      const dismissed = getDismissedIds();
      dismissed.add(id);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify([...dismissed]));
    }
    setVisibleAnnouncement(null);
    dismissAnnouncement();
  };
  
  const { data: response, isLoading, error, refetch } = useQuery<{ data: Session[] }>({
    queryKey: ['sessions'],
    queryFn: () => fetchApi<{ data: Session[] }>('/sessions'),
  });

  const sessions = response?.data;

  // Filter sessions based on search term and filters
  const filteredSessions = sessions
    ? sessions.filter((session) => {
        // Search term matching (title, speaker, description)
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          const matchesSearch =
            session.title.toLowerCase().includes(searchLower) ||
            session.speakerName.toLowerCase().includes(searchLower) ||
            session.description.toLowerCase().includes(searchLower);
          
          if (!matchesSearch) return false;
        }

        // Time slot / date filtering
        if (filters.timeRange) {
          // Check if filter value looks like a date (contains a comma) or a time slot
          const isDateFilter = filters.timeRange.includes(',');
          if (isDateFilter) {
            const sessionDate = new Date(session.startTime).toLocaleDateString('en-US', {
              timeZone: CONFERENCE_TIMEZONE,
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            });
            if (sessionDate !== filters.timeRange) return false;
          } else {
            const sessionTime = new Date(session.startTime).toLocaleTimeString('en-US', {
              timeZone: CONFERENCE_TIMEZONE,
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            });
            if (sessionTime !== filters.timeRange) return false;
          }
        }

        // Location filtering
        if (filters.location) {
          const sessionLocation = `${session.room.building} - ${session.room.name}`;
          const matchesLocation =
            session.room.building === filters.location ||
            sessionLocation === filters.location;
          
          if (!matchesLocation) return false;
        }

        return true;
      })
    : [];

  // Sort sessions by start time
  const sortedSessions = [...filteredSessions].sort((a, b) => 
    new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  // Group sessions by date
  const sessionsByDate = sortedSessions.reduce((acc, session) => {
    const date = new Date(session.startTime).toLocaleDateString('en-US', {
      timeZone: CONFERENCE_TIMEZONE,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(session);
    return acc;
  }, {} as Record<string, Session[]>);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" message="Loading schedule..." />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error instanceof Error ? error.message : 'Failed to load schedule'}
        onRetry={() => refetch()}
      />
    );
  }

  if (!sessions || sessions.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="w-16 h-16 text-usc-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <h3 className="text-xl font-semibold text-usc-gray-700 mb-2">
          No sessions scheduled
        </h3>
        <p className="text-usc-gray-600">
          Check back later for the conference schedule.
        </p>
      </div>
    );
  }

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({
      timeRange: '',
      location: '',
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-usc-garnet mb-2">
          Conference Schedule
        </h1>
        <p className="text-usc-gray-600">
          Browse all sessions and plan your conference experience
        </p>
        
        {/* Real-time connection status indicator */}
        <div className="mt-2 flex items-center gap-2 text-sm">
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`}></div>
          <span className="text-usc-gray-600">
            {isConnected ? 'Live updates active' : 'Connecting...'}
          </span>
        </div>
      </div>

      {/* Announcement banner */}
      {visibleAnnouncement && (
        <AnnouncementBanner announcement={visibleAnnouncement} onDismiss={handleDismiss} />
      )}

      {/* Search and Filter Section */}
      <div className="space-y-4">

        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search by title, speaker, or description..."
        />
        <FilterPanel
          filters={filters}
          onChange={setFilters}
          onClear={handleClearFilters}
          sessions={sessions}
          searchTerm={searchTerm}
        />
      </div>

      {/* No results message */}
      {filteredSessions.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="w-16 h-16 text-usc-gray-400 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <h3 className="text-xl font-semibold text-usc-gray-700 mb-2">
            No sessions found
          </h3>
          <p className="text-usc-gray-600">
            Try adjusting your search or filters
          </p>
        </div>
      )}

      {Object.entries(sessionsByDate).map(([date, dateSessions]) => {
        // Group concurrent sessions within each date
        const concurrentGroups = groupConcurrentSessions(dateSessions);
        
        return (
          <div key={date} className="space-y-6">
            <h2 className="text-2xl font-bold text-usc-gray-900 border-b-2 border-usc-garnet pb-2">
              {date}
            </h2>
            {concurrentGroups.map((group, groupIndex) => {
              // Format time slot for the group
              const earliestStart = group.reduce((earliest, session) => {
                const sessionStart = new Date(session.startTime).getTime();
                return sessionStart < earliest ? sessionStart : earliest;
              }, new Date(group[0].startTime).getTime());
              
              const timeSlot = new Date(earliestStart).toLocaleTimeString('en-US', {
                timeZone: CONFERENCE_TIMEZONE,
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              });
              
              return (
                <div key={groupIndex} className="space-y-3">
                  {group.length > 1 && (
                    <h3 className="text-lg font-semibold text-usc-gray-700">
                      {timeSlot} - Concurrent Sessions
                    </h3>
                  )}
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {group.map((session) => (
                      <SessionCard 
                        key={session.id} 
                        session={session}
                        onClick={() => setSelectedSession(session)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      
      {selectedSession && (
        <SessionDetail 
          session={selectedSession} 
          onClose={() => setSelectedSession(null)}
        />
      )}
    </div>
  );
}
